import React, { useState, useEffect, useCallback, useRef } from 'react';
import useApi from '../hooks/useApi';
import { Upload, Image, Loader2, Save, CheckCircle, XCircle } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Estilos do editor

// Configuração da barra de ferramentas do editor
const modules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['clean']
    ],
};

const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet', 'align', 'size'
];

// --- Sub-componente: ImageUploader (MANTIDO IGUAL) ---
const ImageUploader = ({ apiFetch, onDataChange, currentImagePath, fieldKey, title }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        setPreview(null);
        setFile(null);
    }, [currentImagePath]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreview(URL.createObjectURL(selectedFile));
            setError('');
        }
    };

    const handleUpload = async () => {
        if (!file) return;
        setLoading(true);
        setError('');

        const formData = new FormData();
        formData.append('assinatura', file);
        formData.append('field', fieldKey);

        try {
            await apiFetch('/layout-settings/upload-assinatura', {
                method: 'POST',
                body: formData,
            });
            onDataChange();
        } catch (err) {
            console.error(`Erro no upload (${fieldKey}):`, err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const imageUrl = preview || (currentImagePath ? `/api/uploads/layout_images/${currentImagePath}?t=${new Date().getTime()}` : null);

    return (
        <div className="p-4 border rounded-lg">
            <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
            <div className="mt-4 h-40 flex items-center justify-center bg-gray-100 rounded-md overflow-hidden">
                {imageUrl ? (
                    <img src={imageUrl} alt="Pré-visualização" className="h-full w-full object-contain" />
                ) : (
                    <Image size={48} className="text-gray-400" />
                )}
            </div>
            <input 
                type="file" 
                accept="image/png, image/jpeg" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                className="hidden" 
            />
            <button
                type="button"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 mt-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
                <Upload size={16} /> Selecionar Imagem
            </button>
            {file && (
                <button
                    type="button"
                    onClick={handleUpload}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 mt-2 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                >
                    {loading ? <Loader2 className="animate-spin" /> : <Save size={16} />}
                    Salvar Imagem
                </button>
            )}
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
    );
};


// --- Componente Principal: LayoutSettingsPage ---
const LayoutSettingsPage = ({ apiFetch, onDataChange: notifyAppJs }) => {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState('');

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiFetch('/layout-settings');
            setSettings(data || {});
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // Handler genérico para inputs normais
    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    // Handler para o ReactQuill
    const handleEditorChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmitText = async (e) => {
        e.preventDefault();
        setSaveLoading(true);
        setSaveSuccess(false);
        setSaveError('');

        try {
            const textData = {
                os_nsa_start_number: settings.os_nsa_start_number || 1,
                os_layout_condicoes: settings.os_layout_condicoes || '',
                os_layout_validade: settings.os_layout_validade || '',
                os_layout_obs: settings.os_layout_obs || '',
                os_layout_frase1: settings.os_layout_frase1 || '', // HTML
                os_layout_frase2: settings.os_layout_frase2 || '', // HTML
                os_layout_footer: settings.os_layout_footer || ''   // HTML (Rodapé)
            };
            
            await apiFetch('/layout-settings', {
                method: 'POST',
                body: textData,
            });
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
            notifyAppJs();
        } catch (err) {
            setSaveError(err.message);
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDataChange = () => {
        fetchSettings();
        notifyAppJs();
    };

    if (loading) {
        return <div className="flex justify-center items-center p-8"><Loader2 className="animate-spin" /></div>;
    }

    if (error) {
        return <p className="p-4 text-red-600 bg-red-100 rounded-md">{error}</p>;
    }

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">Configurações de Layout (OS/NSA)</h1>
            
            <form onSubmit={handleSubmitText} className="p-6 bg-white rounded-lg shadow-md mb-8 space-y-6">
                
                {/* 1. Textos Padrão */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-700 border-b pb-2">Textos Padrão</h2>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Condições de Pagamento</label>
                        <ReactQuill 
                            theme="snow"
                            value={settings.os_layout_condicoes || ''}
                            onChange={(val) => handleEditorChange('os_layout_condicoes', val)}
                            modules={modules}
                            formats={formats}
                            className="bg-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prazo de Validade</label>
                        <ReactQuill 
                            theme="snow"
                            value={settings.os_layout_validade || ''}
                            onChange={(val) => handleEditorChange('os_layout_validade', val)}
                            modules={modules}
                            formats={formats}
                            className="bg-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                        <ReactQuill 
                            theme="snow"
                            value={settings.os_layout_obs || ''}
                            onChange={(val) => handleEditorChange('os_layout_obs', val)}
                            modules={modules}
                            formats={formats}
                            className="bg-white"
                        />
                    </div>
                </div>

                {/* 2. Textos do Rodapé (Assinaturas) */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-700 border-b pb-2">Assinaturas</h2>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Frase da Assinatura 1 (Esquerda)</label>
                        <ReactQuill 
                            theme="snow"
                            value={settings.os_layout_frase1 || ''}
                            onChange={(val) => handleEditorChange('os_layout_frase1', val)}
                            modules={modules}
                            formats={formats}
                            className="bg-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Frase da Assinatura 2 (Direita)</label>
                        <ReactQuill 
                            theme="snow"
                            value={settings.os_layout_frase2 || ''}
                            onChange={(val) => handleEditorChange('os_layout_frase2', val)}
                            modules={modules}
                            formats={formats}
                            className="bg-white"
                        />
                    </div>
                </div>

                {/* 3. Rodapé Geral (NOVO) */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-700 border-b pb-2">Rodapé da Página</h2>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Texto do Rodapé (Aparece em todas as páginas)</label>
                        <ReactQuill 
                            theme="snow"
                            value={settings.os_layout_footer || ''}
                            onChange={(val) => handleEditorChange('os_layout_footer', val)}
                            modules={modules}
                            formats={formats}
                            className="bg-white"
                        />
                        <p className="mt-1 text-xs text-gray-500">Ideal para endereço completo, site, telefones de contato, etc.</p>
                    </div>
                </div>

                {/* 4. Configuração Numérica */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-700 border-b pb-2">Configuração Numérica</h2>
                    <div>
                        <label htmlFor="os_nsa_start_number" className="block text-sm font-medium text-gray-700">Número Inicial da OS/NSA</label>
                        <input
                            type="number"
                            id="os_nsa_start_number"
                            name="os_nsa_start_number"
                            value={settings.os_nsa_start_number || ''}
                            onChange={handleChange}
                            className="w-full p-2 mt-1 border rounded-md"
                            placeholder="Ex: 575"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            Este é o número base que será usado para gerar o ID da OS (Ex: 575 + Data).
                        </p>
                    </div>
                </div>

                {/* Botão de Salvar */}
                <div className="flex items-center justify-end gap-4 pt-4 border-t">
                    {saveSuccess && <div className="flex items-center gap-2 text-green-600"><CheckCircle size={16} /> Salvo!</div>}
                    {saveError && <div className="flex items-center gap-2 text-red-600"><XCircle size={16} /> {saveError}</div>}
                    <button
                        type="submit"
                        disabled={saveLoading}
                        className="flex items-center justify-center gap-2 px-4 py-2 font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-indigo-300"
                    >
                        {saveLoading ? <Loader2 className="animate-spin" /> : 'Salvar Alterações de Texto'}
                    </button>
                </div>
            </form>

            {/* Seção de Upload de Assinaturas */}
            <div className="p-6 bg-white rounded-lg shadow-md">
                <h2 className="text-xl font-semibold text-gray-700 border-b pb-2 mb-6">Imagens de Assinatura</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ImageUploader
                        apiFetch={apiFetch}
                        onDataChange={handleDataChange}
                        currentImagePath={settings.os_layout_assinatura1_path}
                        fieldKey="assinatura1"
                        title="Assinatura 1 (Esquerda)"
                    />
                    <ImageUploader
                        apiFetch={apiFetch}
                        onDataChange={handleDataChange}
                        currentImagePath={settings.os_layout_assinatura2_path}
                        fieldKey="assinatura2"
                        title="Assinatura 2 (Direita)"
                    />
                </div>
            </div>
        </div>
    );
};

export default LayoutSettingsPage;